// Story 1.19 — checklist surfaced when the latest POST /delete-request
// returned 422 ACCOUNT_DELETION_INELIGIBLE. Per UX spec Cross-Cutting
// Pattern Rule #4 ("Every error explains itself and offers a path
// forward"), each row names what's missing AND what to do.
//
// The escrow row is the one that matters most: money in escrow is money
// the user owns and cannot see in their balance, so "you have €30 held"
// is useless on its own. When the escrow is buyer-side offer holds the
// row NAMES each offer and links to the withdraw surface; when it is
// seller-side sale proceeds there is nothing to withdraw and the row
// says so instead of sending the user somewhere with no button on it.

import { Link } from "react-router-dom";

import {
  hasCreatorPageAccess,
  useAccountMe,
} from "../admin/useAccountMe";
import type { DeletionError } from "./useRequestDeletion";

function formatEur(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const euros = Math.floor(abs / 100);
  const remainder = abs % 100;
  return `${sign}€${euros}.${remainder.toString().padStart(2, "0")}`;
}

type Row = {
  ok: boolean;
  label: string;
  action: React.ReactNode | null;
};

export type IneligibilityChecklistProps = {
  detail: DeletionError;
};

export function IneligibilityChecklist({ detail }: IneligibilityChecklistProps) {
  // Story 8.11 — the payout surface is guarded. `/account/creator-dashboard`
  // sits behind `ApprovedCreatorGuard`, which admits approved creators and
  // former creators (withdrew after approval) and BOUNCES everyone else to
  // /account/become-creator with a toast. A suspended creator can still be
  // holding earnings, so linking them to a page that ejects them — from the
  // one panel whose whole job is "here is how to resolve this" — is worse
  // than linking the wallet, where the conversion they CAN still use lives.
  // Same predicate the guard uses, from the same helper, so the two cannot
  // drift.
  const me = useAccountMe();
  const payoutSurfaceReachable = hasCreatorPageAccess(me.data?.data);

  const balance = detail.balance_cents ?? 0;
  const earnings = detail.earnings_cents ?? 0;
  const escrow = detail.escrow_cents ?? 0;
  const offers = detail.blocking_offers ?? [];
  const listings = detail.active_listings ?? 0;
  const refunds = detail.in_flight_refunds ?? 0;

  const rows: Row[] = [
    {
      ok: balance === 0,
      label:
        balance === 0
          ? "Wallet balance is €0."
          : `Wallet balance is ${formatEur(balance)} — needs to be €0.`,
      // Story 8.3 — this row changed REMEDY, not just wording. There is no
      // cash-out to send the user to. The two routes to zero are: spend it on
      // Greenhold, or refund a top-up still inside its 14-day PSD2 window.
      // ⚠ Neither covers a balance made of marketplace sale proceeds, or a
      // top-up older than 14 days. For those users this row can never go
      // green, which turns a GDPR Art. 17 erasure into a permanent defer —
      // the residual-balance question recorded as OPEN in
      // 8-3-user-payout-removed.md. Do not paper over it with copy.
      action:
        balance === 0 ? null : (
          <span className="flex flex-col gap-1">
            <Link
              to="/shop"
              className="text-sm underline decoration-dotted"
            >
              Spend it on Greenhold →
            </Link>
            <Link
              to="/account/wallet/refund"
              className="text-sm underline decoration-dotted"
            >
              Or refund a top-up from the last 14 days →
            </Link>
          </span>
        ),
    },
    {
      ok: escrow === 0,
      label:
        escrow === 0
          ? "No funds held in escrow."
          : `${formatEur(escrow)} is held in escrow — this money is yours, and it becomes unreachable if the account is erased while it is locked.`,
      action:
        escrow === 0 ? null : offers.length > 0 ? (
          <span className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">
              Held by {offers.length} live offer{offers.length === 1 ? "" : "s"}
              : {offers.map((o) => formatEur(o.amount_cents)).join(", ")}.
            </span>
            <Link
              to="/account/marketplace/listings"
              className="text-sm underline decoration-dotted"
            >
              Withdraw your offers →
            </Link>
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">
            These are sale proceeds still in escrow. They release
            automatically — wait for them to land in your balance, then
            spend them down.
          </span>
        ),
    },
    {
      ok: listings === 0,
      label:
        listings === 0
          ? "No live marketplace listings."
          : `${listings} live listing${listings === 1 ? "" : "s"} — a sale after erasure would leave the proceeds unreachable.`,
      action:
        listings === 0 ? null : (
          <Link
            to="/account/marketplace/listings"
            className="text-sm underline decoration-dotted"
          >
            Cancel your listings →
          </Link>
        ),
    },
    // Story 8.11 — the EARNED pot. Without this row a creator blocked on
    // earnings saw four green ticks under "Resolve these items first" and no
    // blocking item at all, which is the exact opposite of Rule #4 at the top
    // of this file. It names both exits because they are different remedies:
    // the payout is the one that gets them the money, the conversion is
    // always available and free but lands the amount in the WALLET pot, so
    // the copy says "and spend it" rather than sending the reader into the
    // balance row above.
    {
      ok: earnings === 0,
      label:
        earnings === 0
          ? "No unspent creator earnings."
          : `${formatEur(earnings)} of creator earnings is unspent — after erasure there is no way to reach it.`,
      action:
        earnings === 0 ? null : (
          <span
            className="flex flex-col gap-1"
            data-testid="checklist-earnings-action"
          >
            {payoutSurfaceReachable ? (
              <Link
                to="/account/creator-dashboard"
                className="text-sm underline decoration-dotted"
              >
                Request a payout →
              </Link>
            ) : null}
            <Link
              to="/account/wallet"
              className="text-sm underline decoration-dotted"
            >
              {payoutSurfaceReachable
                ? "Or convert it to wallet credit for free →"
                : "Convert it to wallet credit for free →"}
            </Link>
            <span className="text-sm text-muted-foreground">
              {payoutSurfaceReachable
                ? "Converted credit lands in your wallet balance — spend it on Greenhold to clear the row above."
                : "Your creator account is not active, so a payout cannot be requested. Converted credit lands in your wallet balance — spend it on Greenhold to clear the row above."}
            </span>
          </span>
        ),
    },
    // Story 8.3 — the "SEPA payouts in flight" row is GONE, server side and
    // here, in the same release. With no payout rail the predicate is
    // permanently false; a checklist row that can never be anything but a
    // green tick teaches the reader that a payout rail exists.
    {
      ok: refunds === 0,
      label:
        refunds === 0
          ? "No refunds in flight."
          : `${refunds} refund${refunds === 1 ? "" : "s"} still in flight.`,
      action:
        refunds === 0 ? null : (
          <span className="text-sm text-muted-foreground">
            Wait for our payment partner to confirm the refund settled.
          </span>
        ),
    },
  ];

  return (
    <div className="rounded-lg border border-amber-700/40 bg-amber-50/5 p-4">
      <h3 className="font-heading text-sm font-semibold mb-2">
        Resolve these items first
      </h3>
      <ul className="space-y-2">
        {rows.map((row, i) => (
          <li key={i} className="flex flex-col gap-1 text-sm">
            <span className="flex items-center gap-2">
              <span aria-hidden="true">{row.ok ? "✓" : "✗"}</span>
              <span className={row.ok ? "" : "text-amber-700"}>{row.label}</span>
            </span>
            {row.action && <span className="ml-6">{row.action}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
